import React, { useCallback, useEffect, useState } from 'react';
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    Dialog,
    DialogContentText,
    Typography,
    CircularProgress,
    Box,
    TextField,
} from '@mui/material';
import { getEmailLog, resendEmail } from '@openapi/logs/logs';
import useNotify from '../hooks/useNotify';
import CommonButton from '../components/atom/CommonButton';
import { formatDateTimeInVenueTz } from '../helpers/formatDateTime';
import { SIMPLE_EMAIL_REGEX } from '../helpers/constants';
import { useTranslation } from 'react-i18next';

const DialogHeader = ({ content, t }) => {
    const FIELDS = [
        {
            label: t('emaillogs.modal.date'),
            value: ` ${formatDateTimeInVenueTz(content.created_at)}`,
        },
        { label: t('emaillogs.modal.from'), value: content.user_name || '-' },
        ...(content.from_address
            ? [{ label: t('emaillogs.modal.sender'), value: content.from_address }]
            : []),
        { label: t('emaillogs.modal.to'), value: content.to },
        { label: t('emaillogs.modal.subject'), value: content.subject },
        { label: t('emaillogs.modal.result'), value: content.result || '-' },
    ];
    return (
        <DialogContentText component="div">
            {FIELDS.map(({ label, value }) => (
                <Typography key={label}>
                    <b>{label}:</b> {value}
                </Typography>
            ))}
        </DialogContentText>
    );
};

const EmailLogsModal = ({ title, content, reloadData, showModal, MODAL_TYPE }) => {
    const { t } = useTranslation();
    const [loadingEmail, setLoadingEmail] = useState(true);
    const { notifyError, notifySuccess } = useNotify();
    const closeAction = () => showModal(MODAL_TYPE.NONE);
    const [email, setEmail] = useState(null);
    const [confirmState, setConfirmState] = useState(false);
    const [sending, setSending] = useState(false);
    const [resendTo, setResendTo] = useState(content.to || '');
    const resendToValid = SIMPLE_EMAIL_REGEX.test(resendTo.trim());

    const loadEmail = useCallback(async () => {
        setLoadingEmail(true);
        try {
            const res = await getEmailLog(content.id);
            setEmail(res.html);
        } catch (err) {
            notifyError(err);
        } finally {
            setLoadingEmail(false);
        }
    }, [content.id, notifyError]);

    const resendEmailHandler = async () => {
        setSending(true);
        try {
            await resendEmail(content.id, { to: resendTo.trim() });
            notifySuccess(t('emaillogs.emailHasBeenSentAgain'));
            closeAction();
            reloadData();
        } catch (err) {
            notifyError(err);
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        loadEmail();
    }, [content.id, loadEmail]);

    // Reset the resend overlay state when the modal is reused for another email log.
    useEffect(() => {
        setResendTo(content.to || '');
        setConfirmState(false);
    }, [content.id, content.to]);

    return (
        <Dialog
            open
            onClose={closeAction}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth="xl"
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <DialogHeader content={content} t={t} />
                {loadingEmail ? (
                    <CircularProgress
                        style={{
                            position: 'absolute',
                            top: '80%',
                            left: '50%',
                            marginTop: '-20px',
                            marginLeft: '-20px',
                        }}
                    />
                ) : (
                    <iframe
                        title="HTML content for email"
                        style={{
                            width: '120vh',
                            height: '70vh',
                            border: '1px solid #D3D3D3',
                        }}
                        srcDoc={email}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <CommonButton
                    onClick={closeAction}
                    autoFocus
                    label={t('common.close')}
                    buttonType="secondary"
                />
                <CommonButton
                    onClick={() => setConfirmState(true)}
                    label={t('common.resend')}
                    buttonType="primary"
                />
            </DialogActions>
            {confirmState && (
                <Box
                    sx={{
                        display: 'flex',
                        position: 'absolute',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(232, 236, 241, .90)',
                        left: 0,
                        top: 0,
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <Box textAlign="center">
                        <Typography variant="h6" sx={{ marginBottom: '2rem' }}>
                            {t('emaillogs.areYouSureResend')}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: '2rem' }}>
                            <TextField
                                label={t('emaillogs.modal.to')}
                                value={resendTo}
                                onChange={(e) => setResendTo(e.target.value)}
                                error={!resendToValid}
                                sx={{ width: 360 }}
                            />
                        </Box>
                        <CommonButton
                            onClick={() => setConfirmState(false)}
                            label={t('common.cancel')}
                            buttonType="secondary"
                            sx={{ mr: '1rem' }}
                        />
                        <CommonButton
                            onClick={resendEmailHandler}
                            disabled={sending || !resendToValid}
                            label={sending ? t('common.sending') : t('common.resend')}
                            buttonType="primary"
                        />
                    </Box>
                </Box>
            )}
        </Dialog>
    );
};

export default EmailLogsModal;
